"use client";

import { useEffect, useState } from "react";

type CountdownBlockProps = {
  overline?: string | null;
  heading?: string | null;
  targetDateTime?: string | null;
  caption?: string | null;
  expiredMessage?: string | null;
};

type TimeParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

function getTimeParts(target: number, now: number): TimeParts {
  const total = Math.max(0, target - now);
  const seconds = Math.floor(total / 1000);
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
  };
}

const pad = (value: number) => value.toString().padStart(2, "0");

export function CountdownBlock({
  overline,
  heading,
  targetDateTime,
  caption,
  expiredMessage,
}: CountdownBlockProps): JSX.Element | null {
  const target = targetDateTime ? new Date(targetDateTime).getTime() : null;

  const [parts, setParts] = useState<TimeParts | null>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!target) return;

    const tick = () => {
      const now = Date.now();
      setParts(getTimeParts(target, now));
      setExpired(now >= target);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [target]);

  if (!target) return null;

  const units: { label: string; value: number }[] = parts
    ? [
        { label: "Days", value: parts.days },
        { label: "Hours", value: parts.hours },
        { label: "Minutes", value: parts.minutes },
        { label: "Seconds", value: parts.seconds },
      ]
    : [
        { label: "Days", value: 0 },
        { label: "Hours", value: 0 },
        { label: "Minutes", value: 0 },
        { label: "Seconds", value: 0 },
      ];

  return (
    <section className="-mt-10 bg-brand-primary px-4 py-20 text-white tablet:-mt-16">
      <div className="mx-auto grid max-w-5xl justify-items-center gap-8 text-center">
        {overline ? (
          <p className="text-sm font-medium uppercase tracking-[0.25em] text-white/70">
            {overline}
          </p>
        ) : null}

        {heading ? (
          <h2 className="text-balance text-4xl font-semibold sm:text-5xl md:text-6xl">
            {heading}
          </h2>
        ) : null}

        {expired ? (
          <p className="text-2xl font-medium text-white/90">
            {expiredMessage || "It’s here — Happy New Year!"}
          </p>
        ) : (
          <ol
            className="grid grid-cols-4 gap-3 sm:gap-6"
            aria-label="Countdown"
          >
            {units.map((unit) => (
              <li
                key={unit.label}
                className="grid min-w-[64px] justify-items-center gap-2 rounded-2xl bg-white/5 px-3 py-5 sm:min-w-[104px] sm:px-6"
              >
                <span
                  className="text-4xl font-semibold tabular-nums sm:text-6xl md:text-7xl"
                  suppressHydrationWarning
                >
                  {pad(unit.value)}
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-white/60 sm:text-sm">
                  {unit.label}
                </span>
              </li>
            ))}
          </ol>
        )}

        {caption ? <p className="text-base text-white/70">{caption}</p> : null}
      </div>
    </section>
  );
}
